import type { TrackSourceKind } from '@/lib/db';

import {
  LOGBOOK_FILTER_OPTIONS,
  type LogbookFilter,
  type SortId,
  SORT_OPTIONS,
  type SourceFilter,
} from './filters';
import { SOURCE_LABEL } from './labels';

/**
 * Search, source filter and sort for the flight list — `PLAN.md` §6 Phase 4.
 * Purely controlled: the route's own search params are the source of truth
 * (`flights.index.tsx`), so the URL is shareable and the back button works.
 */

const CONTROL_CLASS =
  'border-border bg-surface rounded-control border px-2.5 py-1.5 text-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

export interface FlightListControlsProps {
  query: string;
  onQueryChange: (value: string) => void;
  source: SourceFilter;
  onSourceChange: (value: SourceFilter) => void;
  sort: SortId;
  onSortChange: (value: SortId) => void;
  logbook: LogbookFilter;
  onLogbookChange: (value: LogbookFilter) => void;
}

export function FlightListControls({
  query,
  onQueryChange,
  source,
  onSourceChange,
  sort,
  onSortChange,
  logbook,
  onLogbookChange,
}: FlightListControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="search"
        className={`${CONTROL_CLASS} min-w-0 flex-1 sm:max-w-72`}
        placeholder="Search by registration, route, callsign or file"
        aria-label="Search flights"
        value={query}
        onChange={(e) => {
          onQueryChange(e.target.value);
        }}
      />
      <label className="flex items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Source</span>
        <select
          className={CONTROL_CLASS}
          value={source}
          onChange={(e) => {
            onSourceChange(e.target.value as SourceFilter);
          }}
        >
          <option value="all">All sources</option>
          {(Object.entries(SOURCE_LABEL) as [TrackSourceKind, string][]).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Logbook</span>
        <select
          className={CONTROL_CLASS}
          value={logbook}
          onChange={(e) => {
            onLogbookChange(e.target.value as LogbookFilter);
          }}
        >
          {LOGBOOK_FILTER_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Sort</span>
        <select
          className={CONTROL_CLASS}
          value={sort}
          onChange={(e) => {
            onSortChange(e.target.value as SortId);
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
